import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useEntitlement } from "@/lib/entitlement";
import { formatPlanLabel, formatStatusLabel } from "@/lib/featureAccess";
import { ShieldCheck, KeyRound, CreditCard, ExternalLink } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import SubscriptionManager from "@/components/SubscriptionManager";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
  const ent = useEntitlement();
  const [profile, setProfile] = useState(null);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/me/profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 10) return toast.error("Password must be at least 10 characters.");
    if (nw !== nw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password/change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword: cur, newPassword: nw }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Password change failed.");
      }
      toast.success(profile?.passwordSet ? "Password updated." : "Password set.");
      setCur(""); setNw(""); setNw2("");
      // Refresh profile
      const p = await fetch(`${API_BASE}/api/auth/me/profile`, { credentials: "include" });
      if (p.ok) setProfile(await p.json());
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!ent.authenticated && !ent.loading) {
    return (
      <div className="max-w-md mx-auto py-8 text-center" data-testid="account-not-signed-in">
        <p>You must be signed in to view this page.</p>
        <Link to="/auth" className="text-[var(--rs-amber)] underline mt-2 inline-block">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-6" data-testid="account-page">
      <h1 className="text-2xl font-semibold mb-1">Account &amp; Subscription</h1>
      <p className="text-[var(--rs-text-3)] text-sm mb-6">Manage your sign-in credentials and subscription.</p>

      {/* Account block */}
      <section className="rs-card p-4 mb-5" data-testid="account-summary">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
          <h2 className="font-semibold">Sign-in</h2>
        </div>
        <div className="text-[12.5px] text-[var(--rs-text-2)] space-y-1">
          <div><span className="text-[var(--rs-text-3)]">Email:</span> <span data-testid="account-email">{ent.email}</span></div>
          <div><span className="text-[var(--rs-text-3)]">Password set:</span>{" "}
            <span data-testid="account-password-set">{profile?.passwordSet ? "Yes" : "No (magic-link only)"}</span>
          </div>
        </div>
      </section>

      {/* Subscription block */}
      <section className="rs-card p-4 mb-5" data-testid="account-subscription">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
          <h2 className="font-semibold">Subscription</h2>
        </div>
        <div className="text-[12.5px] text-[var(--rs-text-2)] space-y-1">
          <div><span className="text-[var(--rs-text-3)]">Current plan:</span>{" "}
            <span data-testid="account-plan-label">{formatPlanLabel(ent)}</span>
          </div>
          {formatStatusLabel(ent) && (
            <div><span className="text-[var(--rs-text-3)]">Status:</span>{" "}
              <span data-testid="account-plan-status">{formatStatusLabel(ent)}</span>
            </div>
          )}
        </div>
        <Link to="/pricing" data-testid="account-manage-plan"
          className="inline-flex items-center gap-1 mt-3 text-[12px] text-[var(--rs-amber)] hover:underline">
          Manage plan <ExternalLink className="w-3 h-3" />
        </Link>
        <SubscriptionManager ent={ent} />
        {ent?.isOwner && (
          <Link to="/admin/churn" data-testid="account-churn-link"
            className="inline-flex items-center gap-1 mt-3 ml-4 text-[12px] text-[var(--rs-amber)] hover:underline">
            Churn dashboard <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </section>

      {/* Change/Set password block */}
      <section className="rs-card p-4" data-testid="account-change-password-card">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
          <h2 className="font-semibold">{profile?.passwordSet ? "Change password" : "Set a password"}</h2>
        </div>
        <p className="text-[var(--rs-text-3)] text-[12px] mb-3">
          {profile?.passwordSet
            ? "Update your sign-in password."
            : "Set a password so you can sign in without waiting for a magic-link email."}
        </p>
        <form onSubmit={submit} className="space-y-3">
          {profile?.passwordSet && (
            <PasswordInput placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} required
              testId="account-input-current" />
          )}
          <PasswordInput placeholder="New password (min 10 chars)" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={10}
            testId="account-input-new" />
          <PasswordInput placeholder="Confirm new password" value={nw2} onChange={(e) => setNw2(e.target.value)} required minLength={10}
            testId="account-input-confirm" />
          <button type="submit" disabled={busy}
            data-testid="account-submit"
            className="w-full h-10 rounded-md font-semibold text-sm"
            style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}>
            {busy ? "Saving…" : (profile?.passwordSet ? "Update password" : "Set password")}
          </button>
        </form>
      </section>
    </div>
  );
}
